import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Skeleton,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import registrationService, {
  isRegistrationWebinarEnded,
} from '../services/registrationService';
import type { Registration } from '../services/registrationService';
import courseService, { CourseType } from '../services/courseService';
import { parseApiDateAsUtc } from '../services/webinarTiming';
import { track } from '../services/analytics';

const STATUS_FILTERS = ['All', 'enrolled', 'in_progress', 'completed'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const STATUS_LABELS: Record<string, string> = {
  All: 'All',
  enrolled: 'Enrolled',
  in_progress: 'In Progress',
  completed: 'Completed',
};

// Map registration status → theme.palette.status key. Lookup happens at render
// time so the resolved colors live in the theme, not in this file.
const STATUS_BADGE_KEY: Record<string, 'enrolled' | 'inProgress' | 'completed'> = {
  enrolled: 'enrolled',
  in_progress: 'inProgress',
  completed: 'completed',
};

interface EnrichedRegistration extends Registration {
  courseUrl?: string;
  startDate?: string;
  endDate?: string;
}

const INITIAL_VISIBLE_COUNT = 5;

function courseTypeKey(courseType: CourseType | undefined): 'webinar' | 'onDemand' {
  return courseType === CourseType.Webinar ? 'webinar' : 'onDemand';
}

const COURSE_TYPE_LABELS: Record<'webinar' | 'onDemand', string> = {
  webinar: 'Live Webinar',
  onDemand: 'On Demand',
};

export default function MyLearning() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState<EnrichedRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<StatusFilter>('All');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let isMounted = true;

    Promise.allSettled([
      registrationService.getUserRegistrations(),
      courseService.getCourses(),
    ]).then(([registrationsResult, coursesResult]) => {
      if (!isMounted) return;

      const data = registrationsResult.status === 'fulfilled' ? registrationsResult.value : [];
      const courses = coursesResult.status === 'fulfilled' ? coursesResult.value : [];
      const courseMetaById = new Map(courses.map((course) => [
        course.id,
        {
          courseUrl: course.url,
          startDate: course.upcomingInstances?.[0]?.startDate ?? course.startDate,
          endDate: course.upcomingInstances?.[0]?.endDate ?? course.endDate,
        },
      ]));

      setRegistrations(
        data
          .filter((r) => r.status !== 'cancelled' && r.status !== 'transferred')
          .map((r) => ({ ...r, ...courseMetaById.get(r.courseId) }))
      );
      setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const filtered =
    activeFilter === 'All'
      ? registrations
      : registrations.filter((r) => r.status === activeFilter);

  const visible = showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore = filtered.length > INITIAL_VISIBLE_COUNT;

  const filterCounts = STATUS_FILTERS.reduce<Record<StatusFilter, number>>((acc, f) => {
    acc[f] = f === 'All' ? registrations.length : registrations.filter((r) => r.status === f).length;
    return acc;
  }, {} as Record<StatusFilter, number>);

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: '28px',
            fontWeight: 700,
            letterSpacing: '-0.022em',
            color: 'text.primary',
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          My Learning
        </Typography>
        <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.5 }}>
          Your enrolled courses and CPE progress
        </Typography>
      </Box>

      {/* Stats strip */}
      {!loading && registrations.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            mb: 3,
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        >
          {([
            { label: 'Enrolled',    count: filterCounts.enrolled,    numColor: 'text.primary' as const },
            { label: 'In Progress', count: filterCounts.in_progress, numColor: 'primary.main' as const },
            { label: 'Completed',   count: filterCounts.completed,   numColor: 'success.main' as const },
          ]).map((stat, idx) => (
            <Box
              key={stat.label}
              sx={{
                flex: 1,
                px: 3,
                py: 1.75,
                borderRight: idx < 2 ? '1px solid rgba(0,0,0,0.12)' : 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: '22px',
                  fontWeight: 700,
                  color: stat.numColor,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.02em',
                  mb: 0.375,
                }}
              >
                {stat.count}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Filter segmented control */}
      <Box
        sx={(theme) => ({
          display: 'inline-flex',
          mb: 3,
          p: '4px',
          bgcolor: theme.palette.tint.neutralMuted,
          borderRadius: '10px',
        })}
      >
        {STATUS_FILTERS.map((filter) => (
          <Box
            key={filter}
            component="button"
            onClick={() => { setActiveFilter(filter); setShowAll(false); }}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              px: '10px',
              py: '5px',
              borderRadius: '7px',
              border: 'none',
              cursor: 'pointer',
              bgcolor: activeFilter === filter ? theme.palette.primary.main : 'transparent',
              boxShadow: activeFilter === filter
                ? '0 1px 3px rgba(67, 122, 43, 0.35), 0 0 0 0.5px rgba(67, 122, 43, 0.18)'
                : 'none',
              transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
              outline: 'none',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
              },
            })}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '13px',
                fontWeight: activeFilter === filter ? 600 : 400,
                color: activeFilter === filter ? 'primary.contrastText' : 'text.secondary',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              {STATUS_LABELS[filter]}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '11px',
                fontWeight: 500,
                color: activeFilter === filter ? 'rgba(255, 255, 255, 0.7)' : 'text.disabled',
                lineHeight: 1,
                userSelect: 'none',
              }}
            >
              {filterCounts[filter]}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Content area */}
      {loading ? (
        <LoadingSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState navigate={navigate} hasAny={registrations.length > 0} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            {visible.map((reg, idx) => (
              <Box key={reg.id}>
                {idx > 0 && <Divider />}
                <RegistrationRow
                  registration={reg}
                  onView={() => navigate(`/app/courses/${reg.courseId}`)}
                />
              </Box>
            ))}
          </Paper>
          {hasMore && !showAll && (
            <Box>
              <Button
                variant="text"
                onClick={() => setShowAll(true)}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  color: 'text.secondary',
                  px: 0,
                  minWidth: 0,
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: 'text.primary',
                    textDecoration: 'underline',
                  },
                }}
              >
                Show all ({filtered.length})
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}
    >
      {[0, 1, 2].map((i) => (
        <Box key={i}>
          {i > 0 && <Divider />}
          <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
                <Skeleton variant="rounded" width={55} height={18} sx={{ borderRadius: '4px' }} />
                <Skeleton variant="rounded" width={70} height={18} sx={{ borderRadius: '4px' }} />
              </Box>
              <Skeleton variant="text" width="55%" height={20} sx={{ mb: 0.25 }} />
              <Skeleton variant="text" width={110} height={15} />
            </Box>
            <Skeleton variant="rounded" width={92} height={28} sx={{ borderRadius: '100px', flexShrink: 0 }} />
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

const pillButtonSx = {
  borderRadius: '100px',
  textTransform: 'none',
  fontSize: '12.5px',
  fontWeight: 600,
  px: 2,
  py: '5px',
  border: '1.5px solid',
  borderColor: 'primary.main',
  color: 'primary.main',
  bgcolor: 'transparent',
  flexShrink: 0,
  letterSpacing: '0.01em',
  '&:hover': {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    borderColor: 'primary.main',
  },
  transition: 'all 0.16s ease',
} as const;

function RegistrationRow({
  registration,
  onView,
}: {
  registration: EnrichedRegistration;
  onView: () => void;
}) {
  const dateOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const dateLabel =
    registration.status === 'completed' && registration.completedAt
      ? `Completed ${new Date(registration.completedAt).toLocaleDateString(undefined, dateOptions)}`
      : `Registered ${new Date(registration.registeredAt).toLocaleDateString(undefined, dateOptions)}`;
  const isWebinar = registration.courseType === CourseType.Webinar;
  const launchUrl = isWebinar
    ? registration.joinUrl
    : registration.courseUrl;
  const launchLabel = isWebinar ? 'Join Now' : 'Launch';
  const statusKey = STATUS_BADGE_KEY[registration.status] ?? 'enrolled';
  const typeKey = courseTypeKey(registration.courseType);

  // Token resolvers — keep colors in the theme, not in this component.
  const statusBadgeSx = (theme: Theme) => {
    const s = theme.palette.status[statusKey];
    return { bg: s.bg, fg: s.fg, dot: s.dot };
  };
  const typeBadgeSx = (theme: Theme) => {
    const t = theme.palette.status[typeKey];
    return { bg: t.bg, fg: t.fg };
  };

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'background-color 0.1s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: 0.625, flexWrap: 'wrap' }}>
          <Box
            component="span"
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '10.5px',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              px: '7px',
              py: '3px',
              borderRadius: '4px',
              bgcolor: statusBadgeSx(theme).bg,
              color: statusBadgeSx(theme).fg,
              lineHeight: 1,
            })}
          >
            <Box
              component="span"
              sx={(theme) => ({
                width: '5px',
                height: '5px',
                borderRadius: '50%',
                bgcolor: statusBadgeSx(theme).dot,
                flexShrink: 0,
                display: 'inline-block',
              })}
            />
            {STATUS_LABELS[registration.status] ?? registration.status}
          </Box>

          <Box
            component="span"
            sx={(theme) => ({
              fontSize: '10.5px',
              fontWeight: 500,
              px: '7px',
              py: '3px',
              borderRadius: '4px',
              bgcolor: typeBadgeSx(theme).bg,
              color: typeBadgeSx(theme).fg,
              lineHeight: 1,
              letterSpacing: '0.01em',
            })}
          >
            {COURSE_TYPE_LABELS[typeKey]}
          </Box>
        </Box>

        {/* Title */}
        <Typography
          sx={{ fontWeight: 600, fontSize: '14.5px', lineHeight: 1.4, color: 'text.primary', mb: 0.375 }}
        >
          {registration.courseTitle}
        </Typography>

        {/* Date */}
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', lineHeight: 1 }}>
          {dateLabel}
        </Typography>
      </Box>

      {/* Action */}
      <Box sx={{ flexShrink: 0 }}>
        {launchUrl ? (
          <Button
            component="a"
            href={launchUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('course_launched', { course_id: String(registration.courseId) })}
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
            sx={pillButtonSx}
          >
            {launchLabel}
          </Button>
        ) : isWebinar ? (
          isRegistrationWebinarEnded(registration) ? (
            <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
              Session ended
            </Typography>
          ) : (
            <Typography
              sx={{
                fontSize: '11.5px',
                color: 'text.secondary',
                textAlign: 'right',
                maxWidth: 130,
                lineHeight: 1.4,
              }}
            >
              Opens {registration.startDate
                ? new Date(parseApiDateAsUtc(registration.startDate).getTime()).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })
                : 'soon'}
            </Typography>
          )
        ) : (
          <Button size="small" onClick={onView} sx={pillButtonSx}>
            View Course
          </Button>
        )}
      </Box>
    </Box>
  );
}

function EmptyState({
  navigate,
  hasAny,
}: {
  navigate: ReturnType<typeof useNavigate>;
  hasAny: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 12,
        gap: 1.5,
        textAlign: 'center',
      }}
    >
      <Box
        sx={(theme) => ({
          width: 44,
          height: 44,
          borderRadius: '12px',
          bgcolor: theme.palette.tint.primarySoft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        })}
      >
        <MenuBookIcon sx={{ fontSize: '22px', color: 'primary.main', opacity: 0.7 }} />
      </Box>
      <Typography sx={{ fontWeight: 600, fontSize: '15px', color: 'text.primary' }}>
        {hasAny ? 'No courses match this filter' : 'No courses yet'}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: 280, lineHeight: 1.55 }}>
        {hasAny
          ? 'Try selecting a different status filter above.'
          : 'Browse the course catalog to find CPE courses that fit your requirements.'}
      </Typography>
      {!hasAny && (
        <Button
          variant="contained"
          onClick={() => navigate('/app/courses')}
          sx={{
            mt: 1.5,
            textTransform: 'none',
            fontSize: '13.5px',
            fontWeight: 500,
            borderRadius: '100px',
            px: 3,
          }}
        >
          Browse Courses
        </Button>
      )}
    </Box>
  );
}
